import type { TaskNode } from "./types";

export interface VersionEdit {
  name: string;        // the resulting version name
  from: string | null; // the previous name, when this line renames one ("old -> new")
}

// Parse the Versions textarea. A plain line is a version; a line "old -> new"
// renames `old` to `new`. Blank lines skipped; final names de-duped, order kept.
export function parseVersionLines(text: string): VersionEdit[] {
  const seen = new Set<string>();
  const out: VersionEdit[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^(.*?)\s*->\s*(.*)$/); // "old -> new"
    let name: string;
    let from: string | null = null;
    if (m && m[1].trim() && m[2].trim()) { from = m[1].trim(); name = m[2].trim(); }
    else name = line; // no arrow (or malformed) → treat the whole line as a plain version
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push({ name, from });
  }
  return out;
}

// Apply parsed edits for a labelled field ("version" | "component"): relink every
// task whose value matches a rename's `from` to the new name, and return the new
// ordered list. Defaults to "version" (back-compatible).
export function applyVersionEdits(tree: TaskNode[], edits: VersionEdit[], field: "version" | "component" = "version"): string[] {
  const renames = new Map<string, string>();
  for (const e of edits) if (e.from && e.from !== e.name) renames.set(e.from, e.name);
  if (renames.size) {
    (function walk(list: TaskNode[]) {
      for (const n of list) {
        const to = renames.get(n[field] || "");
        if (to !== undefined) n[field] = to;
        walk(n.children);
      }
    })(tree);
  }
  return edits.map((e) => e.name);
}
